import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Paper, BottomNavigation, BottomNavigationAction,
  Drawer, Box, Typography, Grid, IconButton,
} from '@mui/material';
import {
  HomeOutlined, BarChartOutlined, LocalHospitalOutlined,
  ChatOutlined, MoreHoriz, ShowChart, WaterDrop,
  Timeline, EmojiEvents, MenuBook, BuildOutlined, Close,
  GrassOutlined,
} from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [moreOpen, setMoreOpen] = useState(false);

  const MAIN_ITEMS = [
    { label: t.nav.dashboard, path: '/dashboard', icon: <HomeOutlined /> },
    { label: t.nav.prices, path: '/prices', icon: <BarChartOutlined /> },
    { label: t.nav.cropDoctor, path: '/crop-doctor', icon: <LocalHospitalOutlined /> },
    { label: t.nav.aiChat, path: '/ai-chat', icon: <ChatOutlined /> },
    { label: t.common.viewMore, path: '__more__', icon: <MoreHoriz /> },
  ];

  const MORE_ITEMS = [
    { label: 'Best Seeds', path: '/seeds', icon: <GrassOutlined />, color: '#2E7D32' },
    { label: t.nav.market, path: '/market-intelligence', icon: <ShowChart />, color: '#1565C0' },
    { label: 'Yield Gap', path: '/yield-gap', icon: <Timeline />, color: '#6A1B9A' },
    { label: 'Water', path: '/water', icon: <WaterDrop />, color: '#0277BD' },
    { label: t.nav.schemes, path: '/scheme-maximizer', icon: <EmojiEvents />, color: '#E65100' },
    { label: t.nav.cropIntel, path: '/encyclopedia', icon: <MenuBook />, color: '#2E7D32' },
    { label: 'Rental', path: '/rental', icon: <BuildOutlined />, color: '#5D4037' },
  ];

  const hiddenPaths = ['/', '/login', '/register', '/admin', '/admin/farmers', '/admin/prices', '/admin/notifications', '/admin/analytics'];
  if (hiddenPaths.some(p => location.pathname === p)) return null;

  const currentValue = MAIN_ITEMS.findIndex(item =>
    item.path !== '__more__' && location.pathname === item.path
  );

  const handleChange = (event, newValue) => {
    const item = MAIN_ITEMS[newValue];
    if (item.path === '__more__') {
      setMoreOpen(true);
    } else {
      navigate(item.path);
    }
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          display: { xs: 'block', md: 'none' },
          borderTop: '1px solid #E0E0E0',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.08)'
        }}
        elevation={0}
      >
        <BottomNavigation
          value={currentValue === -1 ? false : currentValue}
          onChange={handleChange}
          sx={{
            height: 64,
            bgcolor: 'white',
            '& .MuiBottomNavigationAction-root': {
              color: '#9E9E9E',
              minWidth: 0,
              padding: '6px 4px 8px',
              '&.Mui-selected': { color: '#2E7D32' }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '10px',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              '&.Mui-selected': { fontSize: '10px', fontWeight: 700 }
            }
          }}
        >
          {MAIN_ITEMS.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={React.cloneElement(item.icon, {
                sx: {
                  fontSize: 22,
                  color: (item.path !== '__more__' && location.pathname === item.path) ? '#2E7D32' : '#9E9E9E'
                }
              })}
              sx={{ '&.Mui-selected': { '& .MuiSvgIcon-root': { color: '#2E7D32' } } }}
            />
          ))}
        </BottomNavigation>
      </Paper>

      {/* More Drawer */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px 20px 0 0',
            pb: 3,
            display: { md: 'none' },
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700} fontSize={15}>
            {t.common.viewMore}
          </Typography>
          <IconButton size="small" onClick={() => setMoreOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Grid container spacing={0} sx={{ px: 2 }}>
          {MORE_ITEMS.map((item) => (
            <Grid item xs={4} key={item.path}>
              <Box
                onClick={() => { navigate(item.path); setMoreOpen(false); }}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 0.75, py: 1.5, px: 1, cursor: 'pointer', borderRadius: 2,
                  '&:hover': { bgcolor: 'grey.100' },
                  transition: '0.15s',
                }}
              >
                <Box
                  sx={{
                    width: 48, height: 48, borderRadius: '14px',
                    bgcolor: item.color + '18',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {React.cloneElement(item.icon, { sx: { fontSize: 24, color: item.color } })}
                </Box>
                <Typography fontSize={11} fontWeight={600} textAlign="center" color="text.secondary" lineHeight={1.3}>
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Drawer>
    </>
  );
};

export default BottomNav;
